import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DepartmentService } from '../../../services/department.service';
import { CommonModule } from '@angular/common';
import { of, switchMap } from 'rxjs';
import { DepartmentDto, SchedulePeriodDto } from '../../../models/department-dto';
import { TextToSpeechService } from '../../../services/text-to-speech.service';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IntentService } from '../../../services/intent.service';
import { MultiSelectModule } from 'primeng/multiselect';
import { AutoFocusDirective } from '../../../directives/auto-focus.directive';
import { ITooltipComponent } from "../../../helpers/i-tooltip/itooltip/itooltip.component";
import { ReadOnlyIfNoGroupPermissionDirectiveDirective } from '../../../directives/read-only-if-no-group-permission-directive.directive';
import { PermissionService } from '../../../services/permission.service';
import { OrganizationService } from '../../../services/organization.service';

interface TimeSlot {
  day: string;
  start: string;
  end: string;
}

@Component({
  selector: 'app-department-form',
  standalone: true,
  imports: [FormsModule, CommonModule, MultiSelectModule, AutoFocusDirective, NgbTooltipModule, ITooltipComponent, ReadOnlyIfNoGroupPermissionDirectiveDirective],
  templateUrl: './department-form.component.html',
  styleUrl: './department-form.component.scss'
})
export class DepartmentFormComponent implements OnInit {

  @ViewChild('audioPlayer', { static: true }) audioPlayer!: ElementRef<HTMLAudioElement>;
  
  selectedPeriod: SchedulePeriodDto | null = null;
  isScheduleEditMode: boolean = false; // Independent flag for schedule period edit mode

  departments: any[] = []; // Stores available departments
  availableEntities: any[] = []; // Stores available intents
  isLoadingTeam: boolean = false; // Loading state for team members

  department: DepartmentDto = {
    name: '',
    phoneNumber: '',
    serviceDescription: '',
    outOfHoursMessage: '',
    outOfHoursFallbackDepartmentId: null,
    ignorePublicHolidays: null,
    alternativeNames: [],
    escalationTeam: [],
    openingTimes: {
      slots: {}
    },
    schedulePeriods: []
  };

  webQuestions: any[] = [];
  hasPermission: boolean = true; // Assume permission is granted by default

  newPeriodTemplate(): SchedulePeriodDto {
    return {
      clientId: crypto.randomUUID(),
      title: '',
      startDateTime: '',
      endDateTime: '',
      isApplied: false,
      serviceDescription: '',
      outOfHoursMessage: '',
      outOfHoursFallbackDepartmentId: null,
      ignorePublicHolidays: null,
      openingTimes: JSON.parse(JSON.stringify(this.department.openingTimes)) // Clone openingTimes
    }
  };

  daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  isEditMode = false;
  alternativeNamesText: string = '';

  constructor(private departmentService: DepartmentService,
    private intentService: IntentService,
    private organizationService: OrganizationService,
    private permissionService: PermissionService,
    private route: ActivatedRoute,
    private router: Router,
    private textToSpeechService: TextToSpeechService,
    private modalService: NgbModal) { }


  ngOnInit(): void {

    this.loadIntents();

    this.route.paramMap.pipe(
      switchMap(params => {
        const id = params.get('id');
        if (id) {
          this.isEditMode = true;
          return this.departmentService.getDepartmentById(id);  // Fetch department for edit
        }
        return of(this.department);  // Create mode, initialize empty department
      })
    ).subscribe({
      next: (department) => {
        if (this.isEditMode && department) {

          const schedulePeriods = department.schedulePeriods.map((period: { id: any; }) => ({
            ...period,
            clientId: period.id || crypto.randomUUID() // Use `id` as `clientId` for saved items, or generate a new one for new items
          }));

          this.department = { ...department, schedulePeriods: schedulePeriods };
          this.alternativeNamesText = this.department.alternativeNames.join('\n');
        }
        this.initializeOpeningTimes();
        this.loadDepartments();
      },
      error: (error) => console.error('Error loading department:', error)
    });

    const organizationId = this.organizationService.currentOrganizationId;
    this.hasPermission = (organizationId && this.permissionService.hasElevatedPermission(organizationId)) as boolean;

  }

  loadDepartments() {
    this.departmentService.getDepartments().subscribe(
      data => this.departments = 
        data
          .sort((a: any, b: any) => a.name.localeCompare(b.name))  // Sort departments by name
          .filter((department:any) => department.id !== this.department.id),  // Exclude current department from the list
    );
  }

  loadIntents() {
    this.isLoadingTeam = true;

    this.intentService.getEntities().subscribe(data => {
      this.availableEntities = data
    })
      .add(() => {
        this.isLoadingTeam = false;
      });
  }


  initializeOpeningTimes(): void {
    // Ensure each day of the week is represented in openingTimes
    this.daysOfWeek.forEach(day => {
      if (!this.department.openingTimes.slots[day]) {
        this.department.openingTimes.slots[day] = [];
      }
    });
  }



  onSubmit() {

    this.department.alternativeNames = this.alternativeNamesText
      .split('\n')
      .map(name => name.trim())
      .filter(name => name !== '');


    //Populate transformedDepartment.openingTimes.slots with formatted time slots
    this.daysOfWeek.forEach(day => {
      if (this.department.openingTimes.slots[day]) {
        this.department.openingTimes.slots[day] = this.department.openingTimes.slots[day].map(slot => ({
          start: slot.start || null,
          end: slot.end || null
        }));
      }
    });


    if (this.isEditMode) {
      this.departmentService.updateDepartment(this.department.id!, this.department).subscribe({
        next: () => this.router.navigate(['/departments']),
        error: (error) => console.error('Error updating department:', error),
      });
    } else {
      this.departmentService.addDepartment(this.department).subscribe({
        next: () => this.router.navigate(['/departments']),
        error: (error) => console.error('Error creating department:', error),
      });
    }

  }


  cancel() {
    this.router.navigate(['/departments']);
  }



  playText(message?: string): void {

    if (!message) {
      alert('Please enter a text to play.');
      return;
    }

    this.textToSpeechService.convertTextToSpeech(message).subscribe((audioBlob) => {
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('Audio URL generated:', audioUrl);
      this.audioPlayer.nativeElement.src = audioUrl;
      this.audioPlayer.nativeElement.play();
    });
  }

  //#region Schedule Periods

  addNewPeriod(content: any): void {
    this.selectedPeriod = this.newPeriodTemplate();
    this.initializePeriodTimes(this.selectedPeriod);
    this.isScheduleEditMode = false;
    this.modalService.open(content, { size: 'xl', backdrop: 'static' });
  }

  editPeriod(content: any, period: SchedulePeriodDto): void {
    this.selectedPeriod = period;
    this.isScheduleEditMode = true;
    this.modalService.open(content, { size: 'xl', backdrop: 'static' });
  }

  deletePeriod(period: SchedulePeriodDto): void {
    const index = this.department.schedulePeriods.findIndex((p) => p.clientId === period.clientId);
    if (index !== -1) {
      this.department.schedulePeriods.splice(index, 1);
    }
  }

  initializePeriodTimes(period: SchedulePeriodDto): void {
    this.daysOfWeek.forEach(day => {
      if (!period.openingTimes.slots[day]) {
        period.openingTimes.slots[day] = [];
      }
    });
  }


  saveSchedulePeriod(modal: any): void {
    if (!this.selectedPeriod) {
      alert("No schedule period selected.");
      return;
    }


    // Clone the current list of schedule periods and include the selected period
    const schedulePeriods = this.department.schedulePeriods
      .map((p) =>
        p.clientId === this.selectedPeriod!.clientId ? this.selectedPeriod : p
      )
      .filter((p): p is SchedulePeriodDto => p !== null); // Filter out null values

    // If it's a new period, add it to the cloned list
    if (!this.isScheduleEditMode) {
      schedulePeriods.push(this.selectedPeriod);
    }

    const validationError = this.validateSchedulePeriods(schedulePeriods);
    //this.validationError = validationError; // Display error message in the UI

    if (validationError) {
      alert(validationError);
      return;
    }


    if (this.isScheduleEditMode) {
      // Update the existing schedule period in the department's list
      const index = this.department.schedulePeriods.findIndex((p) => p.clientId === this.selectedPeriod?.clientId);
      if (index !== -1) {
        this.department.schedulePeriods[index] = { ...this.selectedPeriod };
      }
    } else {
      // Assign a new ID for the new schedule period
      const newPeriod = {
        ...this.selectedPeriod
      };
      this.department.schedulePeriods.push(newPeriod);
    }

    // Close the modal
    modal.close();
  }

  validateSchedulePeriods(schedulePeriods: SchedulePeriodDto[]): string | null {

    if (schedulePeriods.length <= 1) {
      return null; // No overlaps possible
    }

    for (let i = 0; i < schedulePeriods.length; i++) {
      const period = schedulePeriods[i];

      // Check for overlaps with other periods
      for (let j = 0; j < schedulePeriods.length; j++) {
        if (i !== j) {
          const otherPeriod = schedulePeriods[j];

          const isOverlapping =
            period.startDateTime < otherPeriod.endDateTime &&
            period.endDateTime > otherPeriod.startDateTime;

          if (isOverlapping) {
            return `Schedule period "${period.title}" overlaps with "${otherPeriod.title}".`;
          }
        }
      }
    }

    return null; // No validation errors
  }

  //#endregion

  //#region Time Slots Management

  // Helper to get other days
  getOtherDays(currentDay: string): string[] {
    return this.daysOfWeek.filter((day) => day !== currentDay);
  }

  copyTimeSlots(targetDay: string, sourceDay: string, targetOpeningTimes: any): void {
    // Copy slots from sourceDay to targetDay within the provided openingTimes object
    targetOpeningTimes[targetDay] = [...targetOpeningTimes[sourceDay]];
  }

  cloneTimeSlots(sourceDay: string, targetDay: string, targetOpeningTimes: any): void {
    // Clone slots from sourceDay to targetDay within the provided openingTimes object
    this.copyTimeSlots(targetDay, sourceDay, targetOpeningTimes);
  }

  addTimeSlot(day: string, openingTimes: any): void {
    if (!openingTimes[day]) {
      openingTimes[day] = []; // Initialize if the day doesn't exist
    }
    openingTimes[day].push({ start: '', end: '' });
  }

  removeTimeSlot(day: string, index: number, openingTimes: any): void {
    if (openingTimes[day]) {
      openingTimes[day].splice(index, 1);
    }
  }

  // Clone time slots to all weekdays
  cloneToWeekdays(sourceDay: string, openingTimes: any): void {
    const sourceSlots = openingTimes[sourceDay] || [];
    this.daysOfWeek
      .filter(day => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(day))
      .forEach(day => {
        openingTimes[day] = sourceSlots.map((slot: any) => ({ ...slot }));
      });
  }

  // Clone time slots to weekends
  cloneToWeekend(sourceDay: string, openingTimes: any): void {
    const sourceSlots = openingTimes[sourceDay] || [];
    ['Saturday', 'Sunday'].forEach(day => {
      openingTimes[day] = sourceSlots.map((slot: any) => ({ ...slot }));
    });
  }

  //#endregion

}
